<template>
  <ValidationObserver v-slot="{ handleSubmit, valid }">
    <Loading v-if="storeGmbCategoriesLoading" />
    <form v-else class="p-box" @submit.prevent="handleSubmit($event, submit)">
      <div class="p-box__body">
        <Info>
          <p>{{ $t('pages.CompaniesLocationStoreCategoriesEdit.info1') }}</p>
        </Info>
        <div class="container">
          <label class="c-formLabel">
            <p>{{ $t('pages.CompaniesLocationStoreCategoriesEdit.mainCategory') }} {{ $t('pages.CompaniesLocationStoreCategoriesEdit.required') }}</p>
          </label>
          <div class="fieldContainer">
            <ValidationProvider
              v-slot="{ errors, value }"
              v-model="mainCategoryValue"
              rules="required"
              tag="div"
              class="fieldContainer__input"
              :name="$t('pages.CompaniesLocationStoreCategoriesEdit.mainCategory')"
            >
              <SuggestField
                :value="value"
                id-key="category_id"
                :placeholder="$t('labels.placeholder.basic')"
                :suggests="isPossibleSelectCategories(form.mainCategory.gmb_category_id)"
                :current-suggest="form.mainCategory"
                :is-loading="gmbCategoriesLoading"
                @select-suggest="selectSuggest"
                @close-suggest="closeSuggest"
                @focus-field="focusField"
                @input-field="inputField"
              />
              <ValidationError :error="errors[0]" />
            </ValidationProvider>
          </div>
          <div class="repeatField">
            <div
              v-for="(category, index) in subCategoryValues"
              :key="index"
              class="repeatField__item"
            >
              <label class="c-formLabel">
                <p>{{ $t('pages.CompaniesLocationStoreCategoriesEdit.subCategory') }}</p>
              </label>
              <div class="fieldContainer">
                <div class="fieldContainer__input">
                  <SuggestField
                    :id="index"
                    :value="category"
                    id-key="category_id"
                    :placeholder="$t('labels.placeholder.basic')"
                    :suggests="isPossibleSelectCategories(form.subCategories[index].gmb_category_id)"
                    :current-suggest="form.subCategories[index]"
                    :is-loading="gmbCategoriesLoading"
                    @select-suggest="selectSuggest"
                    @close-suggest="closeSuggest"
                    @focus-field="focusField"
                    @input-field="inputField"
                  />
                </div>
                <button
                  type="button"
                  class="fieldContainer__remove"
                  @click="removeField(index)"
                >
                  <img :src="iconCrossPath" alt="">
                  {{ $t('labels.delete') }}
                </button>
              </div>
            </div>
            <button
              v-show="isPossibleAddCategory"
              type="button"
              class="repeatField__add"
              @click="addField"
            >
              <img :src="iconPlusRedPath" alt="">
              <span>{{ $t('labels.add', { 'value': $t('pages.CompaniesLocationStoreCategoriesEdit.anotherCategory') }) }}</span>
            </button>
          </div>
          <ul class="c-checkbox">
            <li class="c-checkbox__item">
              <input
                id="category_synced"
                v-model="form.categorySynced"
                type="checkbox"
                class="c-checkbox__input"
              >
              <label class="c-checkbox__label" for="category_synced">
                {{ $t('pages.CompaniesLocationStoreCategoriesEdit.protect') }}
              </label>
            </li>
          </ul>
          <Info>
            <p>{{ $t('pages.CompaniesLocationStoreCategoriesEdit.info2') }}</p>
          </Info>
          <p class="note">{{ $t('pages.CompaniesLocationStoreCategoriesEdit.note') }}</p>
        </div>
      </div>
      <div class="p-box__footer">
        <div class="c-button c-button--right">
          <router-link
            :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
            class="c-button__item c-button__item--outline-cancel"
          >
            {{ $t('labels.cancel') }}
          </router-link>
          <button
            class="c-button__item c-button__item--primary"
            :class="{'c-button__item--disabled': !valid}" :disabled="!valid"
          >
            {{ $t('labels.save') }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash.debounce'
import Info from '../components/Info'
import Loading from '../components/Loading'
import SuggestField from '../components/SuggestField'
import ValidationError from '../components/ValidationError'
import iconCrossPath from '../../../images/common/icon_cross.svg'
import iconPlusRedPath from '../../../images/common/icon_plus_red.svg'

export default {
  name: 'CompaniesLocationStoreCategoriesEdit',
  components: {
    Info,
    Loading,
    SuggestField,
    ValidationError,
  },
  emits: ['update-title'],
  data() {
    return {
      mainCategoryValue: '',
      subCategoryValues: [],
      form: {
        mainCategory: {},
        subCategories: [],
        categorySynced: null,
      },
      iconCrossPath,
      iconPlusRedPath,
    }
  },
  computed: {
    ...mapState({
      store: state => state.companiesLocationStore.data,
      storeGmbCategories: state => state.companiesStoreGmbCategories.data,
      storeGmbCategoriesLoading: state => state.companiesStoreGmbCategories.loading,
      gmbCategories: state => state.companiesGmbCategories.data,
      gmbCategoriesLoading: state => state.companiesGmbCategories.loading,
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    storeUuid() {
      return this.$route.params.store_uuid
    },
    isPossibleAddCategory() {
      return this.form.subCategories.length < 9
    },
  },
  watch: {
    store(newValue) {
      if (!newValue) return
      this.$emit(
        'update-title',
        this.$t('pages.CompaniesLocationStoreCategoriesEdit.title', { name: newValue.attributes.name })
      )
    },
    storeGmbCategories(newValue) {
      if (!newValue) return
      const mainCategory = newValue.store_gmb_categories.find(category => category.primary)
      const subCategories = newValue.store_gmb_categories.filter(category => !category.primary)
      this.form.categorySynced = newValue.category_synced
      this.form.mainCategory = mainCategory || {}
      this.form.subCategories = subCategories
      this.mainCategoryValue = mainCategory ? mainCategory.name : ''
      this.subCategoryValues = subCategories.map(category => category.name)
    },
  },
  created() {
    this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    this.readStoreGmbCategories({ companyUuid: this.companyUuid, storeUuid: this.storeUuid })
  },
  mounted() {
    this.debouncedReadGmbCategories = debounce(this.readGmbCategories, 1000)
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      readStoreGmbCategories: 'companiesStoreGmbCategories/readGmbCategories',
      updateStoreGmbCategories: 'companiesStoreGmbCategories/updateGmbCategories',
      readGmbCategories: 'companiesGmbCategories/readGmbCategories',
      clearGmbCategories: 'companiesGmbCategories/clearGmbCategories',
    }),
    submit() {
      const payload = {
        store_gmb_categories: [],
        store_gmb_location: {
          category_synced: this.form.categorySynced
        }
      }
      const mainCategoryId = this.form.mainCategory.gmb_category_id || this.form.mainCategory.category_id
      if (mainCategoryId) {
        payload.store_gmb_categories.push({
          gmb_category_id: mainCategoryId,
          primary: true,
        })
      }
      if (this.form.subCategories.length) {
        this.form.subCategories.map(category => {
          const categoryId = category.gmb_category_id || category.category_id
          if (categoryId) {
            payload.store_gmb_categories.push({
              gmb_category_id: category.gmb_category_id || category.category_id,
              primary: false,
            })
          }
        })
      }
      this.updateStoreGmbCategories({
        companyUuid: this.companyUuid,
        storeUuid: this.storeUuid,
        payload,
      })
    },
    selectSuggest({ suggest, id }) {
      if (id || id === 0) {
        this.form.subCategories[id] = suggest
        this.subCategoryValues[id] = suggest.name
      } else {
        this.form.mainCategory = suggest
        this.mainCategoryValue = suggest.name
      }
    },
    closeSuggest(id) {
      if (id || id === 0) {
        this.subCategoryValues[id] = this.form.subCategories[id].name || ''
      } else {
        this.mainCategoryValue = this.form.mainCategory.name || ''
      }
    },
    focusField(value) {
      if (value.trim()) {
        this.debouncedReadGmbCategories({ companyUuid: this.companyUuid, query: value })
      } else {
        this.clearGmbCategories()
      }
    },
    inputField({ value, id }) {
      if (value.trim()) this.debouncedReadGmbCategories({ companyUuid: this.companyUuid, query: value })
      if (id || id === 0) {
        this.subCategoryValues[id] = value
      } else {
        this.mainCategoryValue = value
      }
    },
    addField() {
      if (!this.isPossibleAddCategory) return
      this.subCategoryValues.push('')
      this.form.subCategories.push({})
    },
    removeField(id) {
      this.subCategoryValues = this.subCategoryValues.filter((category, index) => index !== id)
      this.form.subCategories = this.form.subCategories.filter((category, index) => index !== id)
    },
    isPossibleSelectCategories(id) {
      let categoryIds = []
      if (this.form.mainCategory.gmb_category_id) {
        categoryIds.push(this.form.mainCategory.gmb_category_id)
      }
      if (this.form.subCategories.length) {
        this.form.subCategories.map(category => {
          if (category.gmb_category_id) categoryIds.push(category.gmb_category_id)
        })
      }
      categoryIds = categoryIds.filter(categoryId => categoryId !== id)
      return this.gmbCategories.filter(category => !categoryIds.includes(category.category_id))
    }
  },
}
</script>

<style lang="scss" scoped>
.note {
  color: #E9446A;
  font-weight: bold;
  font-size: 12px;
  line-height: 1.5;
}
.fieldContainer {
  display: flex;
  align-items: center;
  &__input {
    max-width: 320px;
  }
  &__remove {
    display: inline-flex;
    align-items: center;
    margin-left: 24px;
    font-size: 12px;
    line-height: 1.5;
    img {
      width: 16px;
      margin-right: 10px;
    }
  }
}
.container {
  padding: 24px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  .c-formLabel,
  .c-checkbox {
    &:not(:first-child) {
      margin-top: 24px;
    }
  }
  .c-info {
    margin: 16px 0 0;
    &:not(:last-child) {
      margin-bottom: 0;
    }
  }
  .note {
    margin-top: 16px;
  }
}
.c-info:not(:last-child) {
  margin-bottom: 24px;
}
.repeatField {
  &__item {
    margin-top: 24px;
  }
  &__add {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 24px;
    color: #E9446A;
    font-size: 12px;
    line-height: 1.5;
    img {
      width: 16px;
      margin-right: 10px;
    }
  }
}
</style>
